import React, { useCallback, useEffect } from "react";
import { FLAT_RATE_PLANS, flatRatePlanNames } from "@common/flatRateBilling";
import { SubscriptionType } from "@common/types/billing";
import { Organization } from "@common/types/organization";
import { useTimer } from "@hooks/useTimer";
import { useBillingStatus } from "@hooks/useBillingStatus";
import { useCanManageWorkspaceBilling } from "@hooks/permissions/useCanManageWorkspaceBilling";
import { useScope } from "@state/scopeContext.lib";
import { useCompleteOrganizationOnboardingMutation } from "@api/userOnboarding.api";
import { PageCoverLoader } from "@design-system/PageCoverLoader/PageCoverLoader";
import styles from "./Outro.module.scss";

interface Props {
    organization: Organization;
}

export const AccountantOutro: React.FC<Props> = ({ organization }) => {
    const { isFetched, currentPlan, subscriptionType, bookedEnterpriseCall } =
        useBillingStatus();
    const canManageBilling = useCanManageWorkspaceBilling();
    const completeOrganizationOnboardingMutation =
        useCompleteOrganizationOnboardingMutation();
    const { changeScope } = useScope();

    const onFinish = useCallback(async () => {
        await completeOrganizationOnboardingMutation.mutateAsync(
            organization.id,
        );

        changeScope({
            organizationId: organization.id,
            workspaceId: null,
        });
    }, [organization, changeScope, completeOrganizationOnboardingMutation]);

    const { start } = useTimer(onFinish, 3_000);

    useEffect(() => {
        if (isFetched || organization) {
            start();
        }
    }, [isFetched, start, organization]);

    const currentPlanName = flatRatePlanNames[currentPlan];
    let headline: React.ReactNode;
    let title: React.ReactNode = "Processing your data";

    if (organization && !organization.onboardingCompletedAt) {
        // creating organization
        title = `Creating ${organization.name}'s organization`;
    } else if (
        subscriptionType !== SubscriptionType.FLAT_RATE ||
        currentPlan === FLAT_RATE_PLANS.FREE ||
        !canManageBilling
    ) {
        headline = "Get ready to kick back...";
    } else if (bookedEnterpriseCall) {
        title = "You're all set for your call";
        headline = <>Start exploring</>;
    } else {
        headline = (
            <>
                Your {currentPlanName} plan trial is about to start, get ready
                to kick back...
            </>
        );
    }

    return (
        <PageCoverLoader backdrop className={styles.cover} logo={false}>
            <div className={styles.outro}>
                <h1 className="onboarding__title">{title}</h1>
                {headline && <p className="onboarding__headline">{headline}</p>}
            </div>
        </PageCoverLoader>
    );
};
